// src/components/InputSuggestionWithExposeRef.jsx
import React, { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import { Input, List, ListItem, Box, Spinner } from "@chakra-ui/react";
import axios from "axios";
import debounce from "lodash.debounce";

const InputSuggestionWithExposeRef = forwardRef(
  ({ name, placeholder, value: propValue, onChange, onSelect, onBlur, fromOrTo, filter = "cities", airportTripType, isDisabled }, ref) => {
    const [inputValue, setInputValue] = useState(propValue || "");
    const [suggestions, setSuggestions] = useState([]);
    const [recentSearches, setRecentSearches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [showDropdown, setShowDropdown] = useState(false);

    // Debug: Log filter changes
    useEffect(() => {
      // console.log(`[${name}] Filter prop changed to:`, filter, 'airportTripType:', airportTripType);
    }, [filter, name, airportTripType]);

    // Load recent searches on mount
    useEffect(() => {
      // Simplified storage: one key for airports, one for regular locations
      const isAirportField = filter === 'airport' || filter === 'airport_with_terminal';
      const storageKey = isAirportField ? 'recentAirportLocations' : (airportTripType ? 'recentAirportAddresses' : 'recentRegularLocations');
      
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const allSearches = JSON.parse(stored);
          setRecentSearches(allSearches);
        } else {
          setRecentSearches([]);
        }
      } catch (e) {
        setRecentSearches([]);
      }
    }, [filter]);

    // Expose getValue & setValue to parent
    useImperativeHandle(ref, () => ({
      getValue: () => inputValue,
      setValue: (val) => {
        setInputValue(val);
        onChange?.({ target: { name, value: val } });
      },
    }));

    // Sync with propValue changes from parent
    useEffect(() => {
      if (propValue !== undefined && propValue !== inputValue) {
        setInputValue(propValue);
      }
    }, [propValue]);

    // Fetch suggestions from API - recreate when filter changes
    const fetchSuggestions = React.useCallback(async (query) => {
      const trimmed = query.trim();
      if (!trimmed) return setSuggestions([]);
      setLoading(true);
      try {
        const res = await axios.get(
          `${import.meta.env.VITE_API_URL}/management/getplacesuggestions?input=${encodeURIComponent(trimmed)}&filter=${filter}&country=in`
        );
        // console.log("Fetching with filter:", filter, "for query:", trimmed, "country: in");
        // Backend returns data with place_id and description
        const suggestions = res.data?.data?.map((p) => ({
          name: p.description,
          place_id: p.place_id
        })) || [];
        setSuggestions(suggestions);
      } catch (err) {
        console.error("City suggestions error:", err);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, [filter]);

    // Debounced fetch - recreate when fetchSuggestions changes
    const debouncedFetch = React.useMemo(() => {
      // console.log('Creating debounced fetch with filter:', filter);
      return debounce(fetchSuggestions, 300);
    }, [fetchSuggestions, filter]);

    // Clear suggestions when filter changes
    useEffect(() => {
      setSuggestions([]);
      // console.log('Filter changed to:', filter);
    }, [filter]);

    const handleChange = (e) => {
      let val = e.target.value;
      
      // Sanitize location input: allow letters, numbers, spaces, and common punctuation
      // Remove special characters that don't belong in location names
      val = val.replace(/[^a-zA-Z0-9\s\-',./&()]/g, '');
      
      // Limit consecutive spaces
      val = val.replace(/\s{2,}/g, ' ');
      
      setInputValue(val);
      onChange?.({ target: { name, value: val } });
      setSelectedIndex(-1); // Reset selection when typing
      setShowDropdown(true);
      debouncedFetch(val);
    };

    const handleSelect = (cityName, isAirport = false) => {
      setInputValue(cityName);
      setSuggestions([]);
      setSelectedIndex(-1);
      setShowDropdown(false);
      onChange?.({ target: { name, value: cityName } });
      onSelect?.(cityName);
      
      // Save to recent searches - simplified storage based on location type
      const isAirportField = filter === 'airport' || filter === 'airport_with_terminal';
      const storageKey = isAirportField ? 'recentAirportLocations' : (airportTripType ? 'recentAirportAddresses' : 'recentRegularLocations');
      
      try {
        let recent = [];
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          recent = JSON.parse(stored);
        }
        
        // Remove if already exists
        recent = recent.filter(item => {
          const itemName = typeof item === 'object' ? item.name : item;
          return itemName !== cityName;
        });
        
        // Add new search with metadata at the beginning
        recent.unshift({
          name: cityName,
          isAirport: isAirport
        });
        
        // Keep only last 10 searches
        recent = recent.slice(0, 10);
        
        localStorage.setItem(storageKey, JSON.stringify(recent));
      } catch (e) {
        console.error('Error saving recent search:', e);
      }
    };

    const handleFocus = () => {
      setShowDropdown(true);
    };

    const handleBlur = () => {
      // Delay to allow click on suggestion
      setTimeout(() => setShowDropdown(false), 200);
      // Call parent's onBlur handler if provided
      if (onBlur) {
        onBlur();
      }
    };

    const handlePaste = (e) => {
      e.preventDefault();
      const pastedText = (e.clipboardData || window.clipboardData).getData("text");
      
      // Sanitize pasted content
      let sanitized = pastedText.replace(/[^a-zA-Z0-9\s\-',./&()]/g, '');
      sanitized = sanitized.replace(/\s{2,}/g, ' ').trim();
      
      // Insert sanitized text at cursor position
      const target = e.target;
      const start = target.selectionStart;
      const end = target.selectionEnd;
      
      const newValue = inputValue.substring(0, start) + sanitized + inputValue.substring(end);
      setInputValue(newValue);
      onChange?.({ target: { name, value: newValue } });
      setSelectedIndex(-1);
      debouncedFetch(newValue);
    };

    const handleKeyDown = (e) => {
      const totalItems = (inputValue ? suggestions.length : recentSearches.length);
      if (totalItems === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) => 
            prev < totalItems - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < totalItems) {
            if (inputValue) {
              const item = suggestions[selectedIndex];
              const isAirport = filter === 'airport' || filter === 'airport_with_terminal';
              handleSelect(item.name, isAirport);
            } else {
              const item = recentSearches[selectedIndex];
              const cityName = typeof item === 'object' ? item.name : item;
              const isAirport = typeof item === 'object' ? item.isAirport : false;
              handleSelect(cityName, isAirport);
            }
          }
          break;
        case "Escape":
          e.preventDefault();
          setSuggestions([]);
          setShowDropdown(false);
          setSelectedIndex(-1);
          break;
        default:
          break;
      }
    };

    return (
      <Box position="relative">
        <Input
          placeholder={placeholder}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onPaste={handlePaste}
          autoComplete="off"
          isDisabled={isDisabled}
        />
        {loading && (
          <Spinner
            size="sm"
            position="absolute"
            top="50%"
            right="10px"
            transform="translateY(-50%)"
          />
        )}
        {showDropdown && (suggestions.length > 0 || (!inputValue && recentSearches.length > 0)) && (
          <List
            position="absolute"
            zIndex={10}
            bg="white"
            border="1px solid #ccc"
            borderRadius="md"
            width="100%"
            maxHeight="200px"
            overflowY="auto"
            mt={1}
            boxShadow="md"
          >
            {/* Show suggestions if typing, otherwise show recent searches */}
            {inputValue ? (
              suggestions.map((city, index) => {
                // Check if this is an airport based on filter
                const isAirport = filter === 'airport' || filter === 'airport_with_terminal';
                return (
                  <ListItem
                    key={index}
                    px={3}
                    py={2}
                    cursor="pointer"
                    bg={selectedIndex === index ? "blue.100" : "white"}
                    _hover={{ bg: "gray.100" }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(city.name, isAirport);
                    }}
                  >
                    {city.name}
                  </ListItem>
                );
              })
            ) : (
              <>
                <ListItem
                  px={3}
                  py={1}
                  fontSize="xs"
                  fontWeight="600"
                  color="gray.500"
                  bg="gray.50"
                  pointerEvents="none"
                >
                  Recent Searches
                </ListItem>
                {recentSearches.map((city, index) => {
                  const cityName = typeof city === 'object' ? city.name : city;
                  const isAirport = typeof city === 'object' ? city.isAirport : false;
                  return (
                    <ListItem
                      key={index}
                      px={3}
                      py={2}
                      cursor="pointer"
                      bg={selectedIndex === index ? "blue.100" : "white"}
                      _hover={{ bg: "gray.100" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelect(cityName, isAirport);
                      }}
                    >
                      {cityName}
                    </ListItem>
                  );
                })}
              </>
            )}
          </List>
        )}
      </Box>
    );
  }
);

export default InputSuggestionWithExposeRef;
